import React, { useMemo, type ElementType } from "react";
import {
  Box,
  Button,
  Flex,
  Spinner,
  Text,
  useColorModeValue,
} from "@chakra-ui/react";
import Card from "components/card/Card";
import LineChart from "components/charts/LineChart";
import { Mark } from "components/icons/Mark";
import { IoCheckmarkCircle } from "react-icons/io5";
import { MdBarChart, MdOutlineCalendarToday } from "react-icons/md";
import { RiArrowDownSFill, RiArrowUpSFill } from "react-icons/ri";
import type { WineSalesMonthly } from "types/fulfillment";
import { formatCurrency } from "utils/formatCurrency";

/** Referência estável para o hook de dependências / eslint. */
const EMPTY_WINE_SERIES: { name: string; data: number[] }[] = [
  { name: "Outros", data: [0, 0, 0, 0, 0, 0] },
];

const SERIES_COLORS: Record<string, string> = {
  Tinto: "#BE2C55",
  Seco: "#DD6B20",
  Branco: "#39B8FF",
  Rosé: "#D98A9E",
  Espumante: "#7551FF",
  Outros: "#A3AED0",
};

export type TotalSoldChartProps = {
  totalSold: number;
  wineSalesMonthly: WineSalesMonthly | null;
  loading?: boolean;
};

export default function TotalSpent({
  totalSold,
  wineSalesMonthly,
  loading,
  ...rest
}: TotalSoldChartProps & Record<string, unknown>) {
  const textColor = useColorModeValue("secondaryGray.900", "white");
  const textColorSecondary = useColorModeValue("secondaryGray.600", "white");
  const boxBg = useColorModeValue("secondaryGray.300", "whiteAlpha.100");
  const iconColor = useColorModeValue("brand.500", "white");
  const bgButton = useColorModeValue("secondaryGray.300", "whiteAlpha.100");
  const bgHover = useColorModeValue(
    { bg: "secondaryGray.400" },
    { bg: "whiteAlpha.50" }
  );
  const bgFocus = useColorModeValue(
    { bg: "secondaryGray.300" },
    { bg: "whiteAlpha.100" }
  );

  const chartSeries = useMemo(
    () =>
      wineSalesMonthly?.series?.length
        ? wineSalesMonthly.series
        : EMPTY_WINE_SERIES,
    [wineSalesMonthly]
  );
  const mom = wineSalesMonthly?.momTotalPercent;
  const momUp = mom != null && mom >= 0;
  const trendColor = momUp ? "green.500" : "red.500";
  const TrendIcon = momUp ? RiArrowUpSFill : RiArrowDownSFill;

  const chartOptions = useMemo(() => {
    const categories = wineSalesMonthly?.categories ?? [];
    const colors =
      chartSeries.length > 0
        ? chartSeries.map((s) => SERIES_COLORS[s.name] ?? "#A3AED0")
        : ["#4318FF", "#39B8FF"];

    return {
      chart: {
        toolbar: { show: false },
        zoom: { enabled: false },
        redrawOnParentResize: true,
        width: "100%",
        dropShadow: {
          enabled: chartSeries.length <= 2,
          top: 10,
          left: 0,
          blur: 8,
          opacity: 0.08,
          color: "#4318FF",
        },
      },
      colors,
      markers: {
        size: 0,
        colors: "white",
        strokeColors: colors,
        strokeWidth: 2,
        strokeOpacity: 0.85,
        strokeDashArray: 0,
        fillOpacity: 1,
        discrete: [],
        shape: "circle" as const,
        radius: 2,
        offsetX: 0,
        offsetY: 0,
        showNullDataPoints: true,
      },
      tooltip: {
        theme: "dark" as const,
        y: {
          formatter: (val: number) => formatCurrency(val),
        },
      },
      dataLabels: { enabled: false },
      stroke: { curve: "smooth" as const, width: 2 },
      xaxis: {
        type: "category" as const,
        categories,
        labels: {
          style: {
            colors: "#A3AED0",
            fontSize: "11px",
            fontWeight: "500",
          },
        },
        axisBorder: { show: false },
        axisTicks: { show: false },
      },
      yaxis: { show: false },
      legend: {
        show: true,
        position: "bottom" as const,
        horizontalAlign: "center" as const,
        fontSize: "11px",
        fontWeight: 500,
        markers: { size: 4, radius: 12 },
        itemMargin: { horizontal: 8, vertical: 4 },
      },
      grid: { show: false },
    };
  }, [wineSalesMonthly, chartSeries]);

  return (
    <Card
      justifyContent="center"
      alignItems="center"
      flexDirection="column"
      w="100%"
      maxW="100%"
      overflow="hidden"
      mb="0px"
      {...rest}
    >
      <Flex justify="space-between" ps="0px" pe="20px" pt="5px" w="100%">
        <Flex align="center" w="100%">
          <Button
            bg={boxBg}
            fontSize="sm"
            fontWeight="500"
            color={textColorSecondary}
            borderRadius="7px"
          >
            <Mark
              as={MdOutlineCalendarToday as ElementType}
              color={textColorSecondary}
              w="18px"
              h="18px"
              me="4px"
            />
            Últimos 6 meses
          </Button>
          <Button
            ms="auto"
            display="flex"
            alignItems="center"
            justifyContent="center"
            bg={bgButton}
            _hover={bgHover}
            _focus={bgFocus}
            _active={bgFocus}
            w="37px"
            h="37px"
            lineHeight="100%"
            borderRadius="10px"
            aria-label="Resumo em barras"
          >
            <Mark as={MdBarChart as ElementType} color={iconColor} w="24px" h="24px" />
          </Button>
        </Flex>
      </Flex>
      <Flex
        w="100%"
        maxW="100%"
        flexDirection={{ base: "column", lg: "row" }}
        overflow="hidden"
        align={{ lg: "stretch" }}
      >
        <Flex
          flexDirection="column"
          me={{ lg: "20px" }}
          mt="28px"
          flexShrink={0}
          maxW={{ lg: "42%" }}
        >
          <Text
            color={textColor}
            fontSize={{ base: "28px", md: "34px" }}
            textAlign="start"
            fontWeight="700"
            lineHeight="100%"
          >
            {formatCurrency(totalSold)}
          </Text>
          <Flex align="center" mb="20px" flexWrap="wrap" gap="6px">
            <Text
              color="secondaryGray.600"
              fontSize="sm"
              fontWeight="500"
              mt="4px"
            >
              Total vendido
            </Text>
            {mom != null ? (
              <Flex align="center">
                <Mark
                  as={TrendIcon as ElementType}
                  color={trendColor}
                  w="18px"
                  h="18px"
                  me="2px"
                  mt="2px"
                />
                <Text color={trendColor} fontSize="sm" fontWeight="700">
                  {momUp ? "+" : ""}
                  {mom.toLocaleString("pt-BR", {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })}
                  %
                </Text>
                <Text color={textColorSecondary} fontSize="xs" ms="4px">
                  vs. mês anterior
                </Text>
              </Flex>
            ) : null}
          </Flex>

          <Flex align="center">
            <Mark
              as={IoCheckmarkCircle as ElementType}
              color="green.500"
              w="22px"
              h="22px"
              me="4px"
            />
            <Text color="green.500" fontSize="md" fontWeight="700">
              Por tipo de vinho
            </Text>
          </Flex>
        </Flex>
        <Box
          flex="1"
          minW={0}
          w="100%"
          maxW="100%"
          minH="260px"
          h={{ base: "260px", lg: "280px" }}
          mt={{ base: "20px", lg: "auto" }}
          overflow="hidden"
        >
          {loading || !wineSalesMonthly ? (
            <Flex minH="220px" align="center" justify="center">
              <Spinner color="brand.500" />
            </Flex>
          ) : (
            <LineChart chartData={chartSeries} chartOptions={chartOptions} />
          )}
        </Box>
      </Flex>
    </Card>
  );
}
